/**
 * Which events are attributable to the attacker.
 *
 * The eight highest-value rows in a case can be invisible in a timeline that is
 * numerically dominated by ordinary activity. On IR-20260818-Controller-001 the six
 * fraudulent sends and the two inbox-rule operations that hid the replies sat among 1,641
 * message-trace rows and 3,294 Exchange rows. Consolidation clears the noise AROUND them;
 * this marks THEM.
 *
 * Three grounds, and they are not equal:
 *
 *   'ip'      — the event's own recorded address is one the ANALYST classified malicious.
 *               Strong: it is the analyst's own determination, on this event's own record.
 *
 *   'device'  — the event names a machine that has only ever been seen signing in from
 *               malicious addresses. This is the ONLY route for a device registration:
 *               `initiatedBy` is Microsoft's `Device Registration Service`, the address is
 *               empty and there is no session, so an attacker joining six of their own
 *               machines to the tenant was rendering as six unattributable Microsoft rows.
 *               Stronger than 'session' (a device object was created by whoever controlled
 *               the account; a stolen token does not manufacture one) but weaker than 'ip'
 *               (the device NAME is attacker-supplied, and the inventory is inferred from
 *               sign-in history rather than asserted by the analyst).
 *
 *   'session' — the event's session was observed from a malicious address. This is how a
 *               send relayed through Microsoft substrate becomes attributable at all, since
 *               both sides of such a record carry Microsoft's address. On the reference
 *               case one session spans all NINE malicious addresses — 38 sign-ins, 239
 *               non-interactive authentications, 20 Exchange operations, 6 sends.
 *
 *   'analyst' — the analyst MARKED this row as attacker activity, with a written reason
 *               (v1.189.0, case field `AttackerEventIds`). It exists for rows whose own
 *               recorded address is a Microsoft service's: an authenticator registered
 *               through a stolen session is logged with My Signins' backend address, so the
 *               'ip' test can never fire and the row read as the client's own remediation.
 *               Ranked below 'ip' (the event's own record agrees with the analyst) and above
 *               'device' / 'session' (inference / corroboration). It never touches the IP's
 *               classification — the row is attributed, the address stays what it is.
 *
 * 🔴 A session is CORROBORATION, NOT PROOF, and the distinction must survive to the UI. A
 * stolen token keeps the VICTIM's session id, so the victim's own activity shares the
 * attacker's session by definition — that is what token theft means. Marking a row
 * 'session' says "this happened inside a session the attacker also used", never "the
 * attacker did this". `basis` is therefore always reported, never flattened to a boolean,
 * and callers must render the two differently.
 *
 * Nothing here filters or hides. It labels.
 */

import { eventDeviceNames, eventDeviceIds, eventDeviceLabel } from './deviceRegistration.js';

/** Event collections that can carry a session used from a malicious address. */
const SESSION_SOURCES = [
  'SignInEvents', 'NonInteractiveSignInEvents', 'ExchangeEvents',
  'SharePointEvents', 'GeneralEvents', 'DlpEvents',
];

/**
 * Event collections that record WHICH MACHINE authenticated.
 *
 * Only the two sign-in feeds do. Interactive sign-ins carry the device as
 * `DeviceHostname`, the non-interactive feed as `DeviceName` — reading one name yields a
 * clean, confident, empty inventory, so `eventDeviceNames` reads both (and splits the
 * comma-joined values the non-interactive feed aggregates into).
 */
const DEVICE_SOURCES = ['SignInEvents', 'NonInteractiveSignInEvents'];

const eventIp = (e) => e.ResolvedUserIP || e.IPAddress || e.InferredIP || '';
const eventSession = (e) => e.SessionId || e.AADSessionId || '';

/**
 * Could this row have been attributed to the attacker AT ALL -- does it carry any of the
 * three signals attackerBasis matches on?
 *
 * 🔴 THIS IS WHAT SEPARATES "No" FROM "Not determined", and it must be asked of the SOURCE,
 * never of the outcome. The Brief's checklist used `rows > 0 && attributed === 0` for "not
 * determined", which is the definition of "No" -- the same observation holds for both states,
 * so it never established that the source could not discriminate. On the reference case that
 * printed "Not determined" over 1,261 SharePoint and OneDrive rows of which every single one
 * carries a real address (1,175 of them the user's own residential IP) and 1,219 carry a
 * session: the source discriminates perfectly well, the attacker simply is not in it. The
 * honest answer there is "No".
 *
 * Deliberately built from the same accessors as attackerBasis. A row is capable when it
 * carries an address, a device name, or a session -- i.e. when a match was possible.
 */
export function attackerBasisCapable(event) {
  if (!event) return false;
  if (eventIp(event)) return true;
  for (const dev of eventDeviceNames(event)) { if (dev) return true; }
  return Boolean(eventSession(event) || event.SendSessionId);
}

/**
 * Machines that have ONLY ever been seen from analyst-classified malicious addresses.
 *
 * 🔴 The rule is deliberately absolute — at least one malicious address AND zero others.
 * A device name is supplied by the client, so a name shared with a real machine is
 * ambiguous, and the "zero others" half is the whole safety property. Do NOT relax it to
 * "mostly malicious": that would let one attacker sign-in from a corporate laptop
 * re-label every one of that laptop's rows as the attacker's.
 *
 * An EMPTY address is skipped rather than counted as "another address" — unknown is not
 * contradiction, and counting it would silently disqualify a device whose feed happens to
 * omit the IP. Measured on the reference case: 7 devices qualify (the 6 registered inside
 * the window plus DESKTOP-D61DA9, seen only from 43.166.208.95), and the 3 real machines
 * — 99731-1, LAPTOP-3U8N1T1T, WIN-LHH5OJINTP3 — do not.
 */
export function buildAttackerDevices(results, malicious) {
  return buildAttackerDeviceIndex(results, malicious).names;
}

/**
 * The same qualification, over BOTH key spaces, plus the map that links them.
 *
 * Returns { names:Set, ids:Set, nameById:Map, idsByName:Map, ambiguousNames:Set }.
 *
 * WHY TWO KEY SPACES AND NOT ONE. The machine the attacker joined is NAMED by a
 * directory-audit registration event (`Device.DisplayName`) and IDENTIFIED by a sign-in row
 * (`DeviceAadId`) -- and those two records share no key, because the registration carries the
 * Entra directory OBJECT id, a different GUID for the same machine. So the name is the only
 * thing linking them, and `nameById` is that link, DERIVED from the case's own sign-in rows
 * rather than assumed.
 *
 * AMBIGUITY IS REPORTED, NEVER RESOLVED. If one name maps to several GUIDs (a rename, or two
 * machines sharing a hostname) the link is not determinable from the case data, and picking
 * one would silently attribute one machine's activity to another. Those names land in
 * `ambiguousNames` and the caller declines to bind. Measured 1:1 across all 9 names on
 * IR-20260818-Controller-001, so this branch is covered synthetically.
 */
export function buildAttackerDeviceIndex(results, malicious) {
  const empty = {
    names: new Set(), ids: new Set(),
    nameById: new Map(), idsByName: new Map(), ambiguousNames: new Set(),
  };
  if (!results || !malicious.size) return empty;

  const seenName = new Map();
  const seenId = new Map();
  const idsByName = new Map();
  const nameById = new Map();

  for (const key of DEVICE_SOURCES) {
    for (const e of results[key] || []) {
      if (!e) continue;
      const ip = eventIp(e);
      if (!ip) continue;
      const isMal = malicious.has(ip);
      const nm = eventDeviceNames(e);
      const ids = eventDeviceIds(e);
      for (const dev of nm) {
        const rec = seenName.get(dev) || { mal: 0, other: 0 };
        if (isMal) rec.mal += 1; else rec.other += 1;
        seenName.set(dev, rec);
      }
      for (const id of ids) {
        const rec = seenId.get(id) || { mal: 0, other: 0 };
        if (isMal) rec.mal += 1; else rec.other += 1;
        seenId.set(id, rec);
      }
      // The name<->id link is only learnable from a row carrying EXACTLY ONE of each. A
      // non-interactive row spanning three machines carries three of both and says nothing
      // about which pairs with which; pairing them there would invent a fact.
      if (nm.length === 1 && ids.length === 1) {
        const set = idsByName.get(nm[0]) || new Set();
        set.add(ids[0]);
        idsByName.set(nm[0], set);
      }
    }
  }

  const ambiguousNames = new Set();
  for (const [nm, ids] of idsByName) {
    if (ids.size > 1) { ambiguousNames.add(nm); continue; }
    nameById.set([...ids][0], nm);
  }

  const names = new Set();
  for (const [dev, rec] of seenName) if (rec.mal > 0 && rec.other === 0) names.add(dev);
  const ids = new Set();
  for (const [id, rec] of seenId) if (rec.mal > 0 && rec.other === 0) ids.add(id);
  return { names, ids, nameById, idsByName, ambiguousNames };
}

/**
 * Build the attacker context from the case results and the analyst's classifications.
 * Returns { maliciousIps:Set, attackerSessions:Set, sessionIpCount:Map, attackerDevices:Set }.
 */
export function buildAttackerContext(results, ipClassifications, analystEvents = null, sessionMarks = null) {
  const malicious = new Set(
    Object.entries(ipClassifications || {})
      .filter(([, v]) => v === 'malicious')
      .map(([ip]) => ip));
  return buildAttackerContextFrom(results, malicious, analystEvents, sessionMarks);
}

/**
 * The analyst's attacker-event marks as a Map<eventId, { reason, by, at }>, from the case
 * field (`AttackerEventIds` — a JSON object map, or its already-parsed form). Anything that
 * is not an object with a non-empty reason is dropped: a mark without a reason is not one
 * the report can state.
 */
export function parseAttackerEventMarks(value) {
  let obj = value;
  if (typeof obj === 'string') {
    try { obj = JSON.parse(obj || '{}'); } catch { obj = {}; }
  }
  const out = new Map();
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return out;
  for (const [id, mark] of Object.entries(obj)) {
    if (!id || !mark || typeof mark !== 'object') continue;
    // v1.189.8: a mark carries a VERDICT. Absent = 'attacker' (every mark written before
    // the Safe option). An attacker mark needs a reason (the report states it); a Safe mark
    // does not, exactly like marking an IP Safe.
    const verdict = mark.verdict === 'safe' ? 'safe' : 'attacker';
    const reason = String(mark.reason || '').trim();
    if (verdict === 'attacker' && !reason) continue;
    out.set(id, { verdict, reason, by: String(mark.by || ''), at: String(mark.at || '') });
  }
  return out;
}

/** Only the attacker-verdict marks, for any consumer that tests membership directly. */
export function attackerVerdictMarks(marks) {
  const out = new Map();
  if (!(marks instanceof Map)) return out;
  for (const [id, m] of marks) if (m && m.verdict !== 'safe') out.set(id, m);
  return out;
}

/** The ids of the records the analyst marked Safe. */
export function safeMarkedIds(marks) {
  const out = new Set();
  if (!(marks instanceof Map)) return out;
  for (const [id, m] of marks) if (m && m.verdict === 'safe') out.add(id);
  return out;
}

/**
 * As above, but from a malicious set the caller ALREADY has.
 *
 * The Executive Report fact pack takes its malicious set from the attacker window rather
 * than re-deriving it from the classifications, so it needs this entry point — and it
 * needs it to be THIS function rather than its own copy: a second derivation of "which
 * sessions are the attacker's" would drift from the timeline badge, and the whole reason
 * this module exists is that the report and the screen must not disagree about what is
 * the attacker's.
 */
export function buildAttackerContextFrom(results, malicious, analystEvents = null, sessionMarks = null) {
  const sessions = new Set();
  // Sessions the analyst marked as the attacker's (utils/sessionMarks.js, v1.190.0) are
  // attacker sessions whatever addresses touched them -- that is what the mark says.
  const marks = sessionMarks instanceof Map ? sessionMarks : new Map();
  for (const sid of marks.keys()) sessions.add(sid);
  const sessionIpCount = new Map();
  if (results && malicious.size) {
    for (const key of SESSION_SOURCES) {
      for (const e of results[key] || []) {
        if (!e) continue;
        const s = eventSession(e);
        if (!s) continue;
        // Count DISTINCT malicious addresses per session: a session seen from many
        // attacker addresses is the token-replay signature, and the count is worth
        // surfacing rather than reducing to a yes/no.
        if (malicious.has(eventIp(e))) {
          sessions.add(s);
          const set = sessionIpCount.get(s) || new Set();
          set.add(eventIp(e));
          sessionIpCount.set(s, set);
        }
      }
    }
  }
  return {
    maliciousIps: malicious,
    attackerSessions: sessions,
    sessionIpCount,
    attackerDevices: buildAttackerDevices(results, malicious),
    // Map<eventId, { reason, by, at }> — the analyst's own marks (parseAttackerEventMarks).
    analystEvents: analystEvents instanceof Map ? analystEvents : new Map(),
    // Map<sessionId, { reason, by, at }> — sessions the analyst marked as the attacker's.
    sessionMarks: marks,
  };
}

/**
 * Every event id a timeline row stands for. A rolled-up row carries its constituents on one
 * of three fields — `_collapsedIds` (message-trace recipients), `_actionIds` (one directory
 * action written as several audit rows), `_periodIds` (an access period) — and a mark set on
 * the row applies to all of them, exactly as dismissal does.
 */
export function rowEventIds(event) {
  if (!event) return [];
  for (const k of ['_collapsedIds', '_actionIds', '_periodIds']) {
    const ids = event[k];
    if (Array.isArray(ids) && ids.length) return ids.filter(Boolean);
  }
  return event.EventId ? [event.EventId] : [];
}

/**
 * The analyst mark on this row, if any — set on the row's own id or on ANY event it stands
 * for (see rowEventIds).
 */
export function analystMarkOf(event, ctx) {
  // The ATTACKER mark only. A Safe mark (v1.189.8) is a different claim and must never be
  // read as attribution -- see analystSafeOf.
  const marks = ctx?.analystEvents;
  if (!event || !marks || marks.size === 0) return null;
  const isAttacker = (m) => m && m.verdict !== 'safe';
  if (event.EventId && isAttacker(marks.get(event.EventId))) return marks.get(event.EventId);
  for (const id of rowEventIds(event)) {
    if (isAttacker(marks.get(id))) return marks.get(id);
  }
  return null;
}

/**
 * The analyst's Safe mark on a row (v1.189.8), or null. A row is Safe when any of its
 * records is marked Safe and none is marked as the attacker's.
 */
export function analystSafeOf(event, ctx) {
  const marks = ctx?.analystEvents;
  if (!event || !marks || marks.size === 0) return null;
  if (analystMarkOf(event, ctx)) return null;
  const ids = event.EventId ? [event.EventId, ...rowEventIds(event)] : rowEventIds(event);
  for (const id of ids) {
    const m = marks.get(id);
    if (m && m.verdict === 'safe') return m;
  }
  return null;
}

/**
 * How this event relates to the attacker.
 * @returns null | { basis: 'ip' | 'analyst' | 'device' | 'session', ip?, reason?, by?, at?, device?, session?, sessionIpCount? }
 */
export function attackerBasis(event, ctx) {
  if (!event || !ctx) return null;
  const ip = eventIp(event);
  if (ip && ctx.maliciousIps && ctx.maliciousIps.has(ip)) return { basis: 'ip', ip };
  // The analyst's mark works on a case with NO malicious address too — a row they have
  // attributed in writing is the attacker's whatever else the case holds.
  const mark = analystMarkOf(event, ctx);
  if (mark) return { basis: 'analyst', reason: mark.reason, by: mark.by, at: mark.at, ip };
  // An analyst Safe mark (v1.189.8) is a judgment made about THIS row: it withdraws the
  // weaker, inferred attributions below (device, session). It does not outrank the row's own
  // malicious address -- the control refuses Safe on such a row, and if the address is
  // classified later the address wins, as it does over the attacker mark.
  if (analystSafeOf(event, ctx)) return null;
  // A session the analyst marked as the attacker's (v1.190.0) attributes every row on it,
  // from the sign-in that created it -- on a case with no malicious address too. Still the
  // 'session' basis (the report's closed vocabulary), carrying the analyst's reason so the
  // label and the report can say it was their determination rather than an inference. It
  // ranks below a row's own Safe mark: a judgment about one row beats one about its session.
  const markedSessions = ctx.sessionMarks;
  if (markedSessions && markedSessions.size) {
    const sid = eventSession(event) || event.SendSessionId || '';
    const m = sid ? markedSessions.get(sid) : null;
    if (m) {
      return {
        basis: 'session', session: sid, analystSession: true,
        reason: m.reason, by: m.by, at: m.at,
        sessionIpCount: (ctx.sessionIpCount.get(sid) || new Set()).size,
      };
    }
  }
  if (!ctx.maliciousIps || ctx.maliciousIps.size === 0) return null;
  // Device before session: it is the stronger claim, and it is the only claim available on
  // a device-registration row (no address, no session).
  const devices = ctx.attackerDevices;
  if (devices && devices.size) {
    for (const dev of eventDeviceNames(event)) {
      if (devices.has(dev)) return { basis: 'device', device: eventDeviceLabel(event, dev) };
    }
  }
  // A message-trace row's own address can be Microsoft substrate while the Exchange send
  // it was joined to (v1.152.0) carries the real session — hence SendSessionId.
  const session = eventSession(event) || event.SendSessionId || '';
  if (session && ctx.attackerSessions.has(session)) {
    return {
      basis: 'session',
      session,
      sessionIpCount: (ctx.sessionIpCount.get(session) || new Set()).size,
    };
  }
  return null;
}

/**
 * Operations that, performed by an attacker, ARE the incident rather than evidence of it:
 * sending mail as the victim, and the mail-flow rules that hide the replies. Used only to
 * order/emphasise, never to filter — an operation missing from this list is still marked
 * when its address or session says so.
 */
const HIGH_IMPACT = new Set([
  'new-inboxrule', 'set-inboxrule', 'remove-inboxrule', 'updateinboxrules',
  'new-transportrule', 'set-transportrule',
  'add-mailboxpermission', 'add-recipientpermission',
  'send', 'sendas', 'sendonbehalf', 'sendmail',
  'set-mailbox', 'new-inboxrule (forward)',
]);

export function isHighImpactOperation(event) {
  const op = String(event?.Operation || '').toLowerCase();
  if (HIGH_IMPACT.has(op)) return true;
  // Outbound mail arrives as a message-trace row whose Operation is not a cmdlet name.
  return event?._eventType === 'MessageTrace' && String(event?.Direction || '') === 'Sent';
}

/** Short, honest label for the UI. */
export function attackerBasisLabel(basis) {
  if (!basis) return '';
  if (basis.basis === 'ip') return 'attacker IP';
  if (basis.basis === 'analyst') return 'attacker · analyst';
  if (basis.basis === 'device') return 'attacker device';
  if (basis.analystSession) return 'attacker session · analyst';
  return 'attacker session';
}

export function attackerBasisTitle(basis) {
  if (!basis) return '';
  if (basis.basis === 'ip') {
    return `This event's own recorded address (${basis.ip}) is one you classified Malicious on this case.`;
  }
  if (basis.basis === 'analyst') {
    const who = basis.by ? ` by ${basis.by}` : '';
    const addr = basis.ip
      ? ` Its recorded address (${basis.ip}) is not classified Malicious and keeps its own classification.`
      : '';
    return `Marked as attacker activity${who}: “${basis.reason}”.${addr}`;
  }
  if (basis.basis === 'device') {
    return `This event names the machine ${basis.device}, which on this case has only ever `
      + 'signed in from addresses you classified Malicious — never from any other address. '
      + 'That is what attributes a device registration, which carries no address of its own '
      + "(Microsoft's Device Registration Service performs it) and no session. The device "
      + 'name is supplied by the client, so this is inferred from sign-in history rather '
      + "than from the event's own record.";
  }
  if (basis.analystSession) {
    const who = basis.by ? ` by ${basis.by}` : '';
    return `This event belongs to sign-in session ${String(basis.session).slice(0, 8)}…, which was marked as `
      + `the attacker's${who}: “${basis.reason}”. Every row on that session is attributed from the sign-in `
      + 'that created it. No address on it was classified Malicious by this mark.';
  }
  const n = basis.sessionIpCount || 0;
  return 'This event happened inside a sign-in session that was also used from '
    + `${n === 1 ? 'a malicious address' : `${n} malicious addresses`} on this case`
    + ` (session ${String(basis.session).slice(0, 8)}…). A stolen token keeps the victim's own session id, `
    + 'so this is corroboration that the activity sits in a compromised session — not proof that the '
    + 'attacker performed it.';
}
